// ------------------------------------------------------------------
// 新TOPページ：REVIEW（Judge.me「Featured carousel」）
// 各カードに「完全なレビュー」ボタンを追加し、押したときだけ <dialog> にレビュー全文を表示する。
// カードのそれ以外の場所は商品ページへのリンク（ntop-review.css で商品名リンクを全体に広げている）。
// Judge.me のカードには投稿写真が含まれないため、ポップアップに出すのは本文などの文字情報と商品画像のみ。
// ------------------------------------------------------------------
if (!customElements.get('ntop-review')) {
  class NtopReview extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('[data-review-modal]');
      this.content = this.querySelector('[data-review-content]');
      if (!this.modal || !this.content || typeof this.modal.showModal !== 'function') return;

      this.addButtons();
      // Judge.me 側の初期化でカードが差し替えられた場合にも、ボタンを付け直す
      const body = this.querySelector('.ntop-review__body');
      if (body && 'MutationObserver' in window) {
        new MutationObserver(() => this.addButtons()).observe(body, { childList: true, subtree: true });
      }

      // ボタンは後から追加・差し替えされるので、クリックはセクション全体で受け取る
      this.addEventListener('click', (e) => {
        const button = e.target.closest('[data-review-more]');
        if (!button || !this.contains(button)) return;
        e.preventDefault();
        e.stopPropagation();
        this.open(button.closest('.jdgm-carousel-item'), button);
      });

      this.modal.querySelectorAll('[data-review-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.modal.close());
      });
      // パネルの外（背景）をクリックしたら閉じる
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.modal.close();
      });
      this.modal.addEventListener('close', () => {
        document.documentElement.classList.remove('ntop-modal-open');
        // 閉じたら押したボタンにフォーカスを戻す
        if (this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
      });
    }

    addButtons() {
      this.querySelectorAll('.jdgm-carousel-item').forEach((item) => {
        if (item.querySelector('[data-review-more]')) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ntop-review__more';
        button.setAttribute('data-review-more', '');
        button.setAttribute('aria-haspopup', 'dialog');
        button.textContent = '完全なレビュー';
        item.appendChild(button);
      });
      this.scheduleUpdate();
    }

    // タイトル・本文が省略されているカードだけボタンを表示する。
    // 省略の有無は画面幅（カードの幅）で変わるため、読み込み後・フォント読み込み後・リサイズ時に測り直す
    scheduleUpdate() {
      if (!this.boundUpdate) {
        this.boundUpdate = () => {
          cancelAnimationFrame(this.updateFrame);
          this.updateFrame = requestAnimationFrame(() => this.updateButtons());
        };
        window.addEventListener('resize', this.boundUpdate);
        window.addEventListener('load', this.boundUpdate);
        document.fonts?.ready.then(this.boundUpdate);
        // Judge.me が少し遅れて行数の指定を書き換えるため、その後にも測る
        setTimeout(this.boundUpdate, 1500);
      }
      this.boundUpdate();
    }

    updateButtons() {
      const isClipped = (node) =>
        !!node && (node.scrollHeight > node.clientHeight + 1 || node.scrollWidth > node.clientWidth + 1);
      this.querySelectorAll('.jdgm-carousel-item').forEach((item) => {
        const button = item.querySelector('[data-review-more]');
        if (!button) return;
        const clipped =
          isClipped(item.querySelector('.jdgm-carousel-item__review-title')) ||
          isClipped(item.querySelector('.jdgm-carousel-item__review-body'));
        if (button.hidden === clipped) button.hidden = !clipped;
      });
    }

    open(item, opener) {
      if (!item || this.modal.open) return;
      this.opener = opener;
      this.content.replaceChildren(this.buildContent(item));
      this.modal.showModal();
      document.documentElement.classList.add('ntop-modal-open');
      // 長いレビューでも先頭から読めるように
      this.modal.scrollTop = 0;
    }

    // カード内の要素を複製して、ポップアップ用の中身を組み立てる
    buildContent(item) {
      const pick = (selector) => item.querySelector(selector);
      const text = (selector) => (pick(selector)?.textContent || '').trim();
      const fragment = document.createDocumentFragment();
      const el = (tag, className, textContent) => {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (textContent) node.textContent = textContent;
        return node;
      };

      // 見出し：商品画像＋名前・星・投稿日
      const head = el('div', 'ntop-review-modal__head');
      const image = pick('img.jdgm-carousel-item__product-image');
      const thumb = el('span', 'ntop-review-modal__thumb');
      if (image) {
        const img = document.createElement('img');
        img.src = image.getAttribute('data-src-retina') || image.currentSrc || image.src;
        img.alt = '';
        img.width = 56;
        img.height = 56;
        thumb.appendChild(img);
      }
      head.appendChild(thumb);

      const meta = el('div', 'ntop-review-modal__meta');
      meta.appendChild(el('p', 'ntop-review-modal__name', text('.jdgm-carousel-item__reviewer-name')));
      const rating = pick('.jdgm-carousel-item__review-rating');
      if (rating) {
        const stars = rating.cloneNode(true);
        stars.className = 'ntop-review-modal__rating';
        stars.removeAttribute('style');
        meta.appendChild(stars);
      }
      const date = this.formatDate(pick('.jdgm-carousel-item__timestamp')?.getAttribute('data-time'));
      if (date) meta.appendChild(el('p', 'ntop-review-modal__date', date));
      head.appendChild(meta);
      fragment.appendChild(head);

      // 商品名・タイトル・本文（カードでは行数を省略しているが、ここでは全文）
      const productName = text('.jdgm-carousel-item__product-title');
      if (productName) fragment.appendChild(el('p', 'ntop-review-modal__product', productName));
      const title = text('.jdgm-carousel-item__review-title');
      if (title) fragment.appendChild(el('p', 'ntop-review-modal__title', title));
      const reviewBody = pick('.jdgm-carousel-item__review-body');
      if (reviewBody) {
        const bodyNode = el('div', 'ntop-review-modal__body');
        reviewBody.childNodes.forEach((node) => bodyNode.appendChild(node.cloneNode(true)));
        fragment.appendChild(bodyNode);
      }

      // 商品ページへのボタン
      const productLink = pick('a.jdgm-carousel-item__product');
      if (productLink && productLink.getAttribute('href')) {
        const link = el('a', 'ntop-review-modal__link', 'この商品を見る');
        link.href = productLink.getAttribute('href');
        fragment.appendChild(link);
      }
      return fragment;
    }

    // Judge.me の data-time（MM/DD/YYYY）を YYYY/MM/DD にする。形式が違えばそのまま表示
    formatDate(value) {
      if (!value) return '';
      const m = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (!m) return value;
      return `${m[3]}/${m[1].padStart(2, '0')}/${m[2].padStart(2, '0')}`;
    }
  }

  customElements.define('ntop-review', NtopReview);
}
